import { useEffect, useState } from "react";
import { TgkTextSource } from "../../interfaces/CoObject";
import { Dimension } from "../../../api/models/dimension";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import {
  extractMandatoryDimensionsFromDatasetFields,
  extractOptionalDimensionsFromDatasetFields,
} from "../../../api/object/DatasetField";
import { DimensionDetail } from "../../../api/models/dimension-detail";
import { DatasetField } from "../../../api/models/dataset-field";
import { Scenario } from "../../../api/models/scenario";

export interface CoObjectFieldsTextProps {
  objectSource: TgkTextSource;
  datasetFields?: DatasetField[];
  inheritableParameters?: DimensionDetail[];
  updateCoObjectSourceText: (obj: TgkTextSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  referringScenarios: Scenario;
}

const useCoObjectDimensionsTextPanel = (props: CoObjectFieldsTextProps) => {
  const {
    objectSource,
    datasetFields,
    inheritableParameters,
    updateCoObjectSourceText,
    isEdit,
    isComponentInstance,
    isLocked,
  } = props;
  const [mandatoryDimensions, setMandatoryDimensions] = useState<Dimension[]>([]);
  const [optionalDimensions, setOptionalDimensions] = useState<Dimension[]>([]);

  useEffect(() => {
    const getDimensions = async () => {
      try {
        const mandatoryDimensions = datasetFields ? extractMandatoryDimensionsFromDatasetFields(datasetFields) : [];
        const optionalDimensions = datasetFields ? extractOptionalDimensionsFromDatasetFields(datasetFields) : [];
        setMandatoryDimensions(mandatoryDimensions);
        setOptionalDimensions(optionalDimensions);

        const objSource = { ...objectSource, type: "TGK_TEXT" } as TgkTextSource;
        if (!isEdit && mandatoryDimensions && inheritableParameters && !objSource.dimensions?.length) {
          objSource.dimensions = applyInheritedDimensions(mandatoryDimensions, inheritableParameters);
        }
        updateCoObjectSourceText(objSource, validateFields(objSource, mandatoryDimensions), false);
      } catch (error) {
        // eslint-disable-next-line no-undef
        console.error("Error fetching dimensions:", error);
      }
    };
    getDimensions();
  }, [objectSource?.dataset, datasetFields]);

  const applyInheritedDimensions = (mandatoryDimensions, inheritableParameters) => {
    return mandatoryDimensions
      ?.map((dimension: Dimension) => {
        const matchedParameter = inheritableParameters.find((p) => p.dimensionOid === dimension.oid);
        return matchedParameter
          ? {
              ...matchedParameter,
              inherited: true,
              label: dimension.label,
              type: dimension.type,
            }
          : null;
      })
      .filter(Boolean);
  };

  function updateDimensionFromTree(dimensionItem: Dimension, newDimensions: DimensionDetail[] | undefined) {
    const dimensionToAdd = newDimensions?.map((newDimension) => ({
      ...newDimension,
      dimensionOid: dimensionItem.oid,
      label: dimensionItem.label,
      type: dimensionItem.type,
    }));

    const newSourceDimensions = objectSource?.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid);
    newSourceDimensions.push(...dimensionToAdd);
    const obj: TgkTextSource = {
      ...objectSource,
      type: "TGK_TEXT",
      dimensions: newSourceDimensions,
    };

    updateCoObjectSourceText(obj, validateFields(obj, mandatoryDimensions), true);
  }

  function validateFields(obj: TgkTextSource, mandatoryDimensions: Dimension[]): ObjectPanelTabs {
    const tab = { key: props.tab.key, name: props.tab.name, isValid: false };
    if (!obj || !checkMandatoryDimensions(obj, mandatoryDimensions)) {
      return tab;
    }

    return { ...tab, isValid: true };
  }

  function checkMandatoryDimensions(obj: TgkTextSource, mandatoryDimensions: Dimension[]) {
    const { dimensions } = obj;
    if (!mandatoryDimensions?.length) {
      return { key: props.tab.key, name: props.tab.name, isValid: true };
    }

    // Check if all mandatoryDimensionRoot items are valid
    return mandatoryDimensions.every((mandatoryDimensionRoot) =>
      dimensions?.some((d) => d.dimensionOid === mandatoryDimensionRoot.oid && d.values?.length)
    );
  }

  return {
    objectSource,
    mandatoryDimensions,
    optionalDimensions,
    inheritableParameters,
    updateDimensionFromTree,
    isComponentInstance,
    isLocked,
  };
};

export default useCoObjectDimensionsTextPanel;
